import Link from 'next/link'
import React, { useEffect, useMemo, useState } from 'react'
import { LogoWhite } from '../../icons/LogoWhite'
import { Menu, Transition } from '@headlessui/react'
import { LogoDark } from '../../icons/LogoDark'
import { useZeus } from '../../utils/useZeus'
import { HAS_ACCOUNT_KEY } from '../../utils/constants'
import { GitHub } from '../../icons/GitHub'
import cn from 'classnames'
import { useRouter } from 'next/router'

export const Header = ({ dark = false }) => {
  const { data } = useZeus({
    user: [
      {},
      {
        id: true,
      },
    ],
  })

  const { user } = data ?? {}

  // to speed things up a bit :)
  const [hasAccount, setHasAccount] = useState(false)
  useEffect(() => {
    const account = localStorage.getItem(HAS_ACCOUNT_KEY)
    if (account) {
      setHasAccount(true)
    }
  }, [])
  useEffect(() => {
    if (user) {
      localStorage.setItem(HAS_ACCOUNT_KEY, 'true')
    }
  }, [user])

  return (
    <Menu>
      {({ open }) => (
        <div className={`relative pt-6`}>
          <div className='max-w-6xl px-6 mx-auto sm:px-6'>
            <nav className='relative flex items-center justify-between lg:justify-center'>
              <div className='flex items-center flex-1'>
                <div className='flex items-center justify-between w-full lg:w-auto'>
                  <Link href='/'>
                    <a aria-label='Home'>
                      {dark ? (
                        <LogoWhite
                          className='h-12 lg:h-16'
                          width={138}
                          height={38}
                        />
                      ) : (
                        <LogoDark
                          className='h-12 lg:h-16'
                          width={138}
                          height={38}
                        />
                      )}
                    </a>
                  </Link>
                  <div className='flex items-center mr-0 lg:hidden'>
                    <Menu.Button
                      className={cn(
                        'inline-flex items-center justify-center p-2 transition duration-150 ease-in-out rounded-md focus:outline-none',
                        {
                          ' focus:bg-gray-100 focus:text-gray-500 text-gray opacity-60 hover:text-gray-500 hover:bg-gray-100': !dark,
                          'text-white opacity-60': dark,
                        },
                      )}
                      aria-label='Main menu'
                      aria-haspopup='true'
                    >
                      <Hamburger />
                    </Menu.Button>
                  </div>
                </div>
              </div>
              <div className='hidden lg:flex lg:items-center lg:justify-end'>
                <div className='hidden lg:flex lg:space-x-10'>
                  <NavLink dark={dark} href='/features'>
                    Features
                  </NavLink>
                  <NavLink dark={dark} href='/pricing'>
                    Pricing
                  </NavLink>
                  <NavLink dark={dark} href='/blog'>
                    Blog
                  </NavLink>
                  <NavLink dark={dark} href='/docs'>
                    Docs
                  </NavLink>
                  <NavLink dark={dark} href='/about'>
                    About
                  </NavLink>
                  <div
                    className={cn('border-l-2', {
                      'border-gray': !dark,
                      'border-white opacity-50': dark,
                    })}
                  />
                  {hasAccount && user ? (
                    <NavLink dark={dark} href='/dashboard'>
                      Dashboard
                    </NavLink>
                  ) : (
                    <>
                      <NavLink dark={dark} href='/api/auth/github'>
                        Login
                      </NavLink>
                      <NavLink dark={dark} href='/api/auth/github'>
                        <GitHub className='h-6' />
                        <span className='ml-2'>Sign up</span>
                      </NavLink>
                    </>
                  )}
                </div>
              </div>
            </nav>
          </div>
          <div className='relative top-0 z-10 -mt-16'>
            <Transition
              show={open}
              enter='transition ease-out duration-100'
              enterFrom='transform opacity-0 scale-95'
              enterTo='transform opacity-100 scale-100'
              leave='transition ease-in duration-75'
              leaveFrom='transform opacity-100 scale-100'
              leaveTo='transform opacity-0 scale-95'
            >
              <Menu.Items
                static
                className='absolute inset-x-0 right-0 z-10 px-3 py-2 origin-top-right outline-none'
              >
                <div className='rounded-lg shadow-md'>
                  <div
                    className='overflow-hidden bg-white rounded-lg shadow-xs'
                    role='menu'
                    aria-orientation='vertical'
                    aria-labelledby='main-menu'
                  >
                    <div className='flex items-center justify-between px-5 pt-4'>
                      <Link href='/'>
                        <Menu.Button>
                          <a>
                            <LogoDark className='w-auto h-8' />
                          </a>
                        </Menu.Button>
                      </Link>
                      <div className='-mr-2'>
                        <Menu.Button className='inline-flex items-center justify-center p-2 transition duration-150 ease-in-out rounded-md outline-none text-gray opacity-60 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500'>
                          <Hamburger />
                        </Menu.Button>
                      </div>
                    </div>
                    <div className='px-2 pt-3 pb-3'>
                      <MobileLink href='/features'>Features</MobileLink>
                      <MobileLink href='/pricing'>Pricing</MobileLink>
                      <MobileLink href='/blog'>Blog</MobileLink>
                      <MobileLink href='/docs'>Docs</MobileLink>
                      <MobileLink href='/about'>About</MobileLink>
                      {hasAccount && user ? (
                        <MobileLink href='/dashboard'>Dashboard</MobileLink>
                      ) : (
                        <>
                          <MobileLink href='/api/auth/github'>Login</MobileLink>
                          <MobileLink href='/api/auth/github'>
                            <GitHub className='w-5 h-5' />
                            <span className='ml-3'>Sign up with GitHub</span>
                          </MobileLink>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Menu.Items>
            </Transition>
          </div>
          <div className='pb-16'></div>
        </div>
      )}
    </Menu>
  )
}

function NavLink({ href, children, dark, ...props }) {
  const router = useRouter()
  const isActive = href === router.asPath

  return (
    <Link href={href} {...props}>
      <a
        className={cn(
          'inline-flex items-center text-lg font-medium gotham transition-opacity',
          {
            'text-white': dark,
            'text-gray opacity-40 hover:opacity-70': !dark && !isActive,
            'text-gray opacity-70': !dark && isActive,
          },
        )}
      >
        {children}
      </a>
    </Link>
  )
}

function MobileLink({ href, children, ...props }) {
  return (
    <Link href={href} {...props}>
      <a
        className='flex items-center px-3 py-2 text-base font-medium transition duration-150 ease-in-out rounded-md text-gray gotham hover:opacity-80 hover:bg-gray-50 focus:outline-none focus:bg-gray-50'
        role='menuitem'
      >
        {children}
      </a>
    </Link>
  )
}

function Hamburger(props) {
  return (
    <svg
      width={24}
      height={26}
      stroke='currentColor'
      viewBox='0 0 24 26'
      fill='none'
      className='w-6 h-6'
      {...props}
    >
      <path strokeWidth={2} d='M0 1L24 1' />
      <path strokeWidth={2} d='M0 13L24 13' />
      <path strokeWidth={2} d='M0 25L24 25' />
    </svg>
  )
}
